/**
 * The API's origin, and the only place `VITE_API_BASE_URL` is read.
 *
 * **It is an ORIGIN, never a path.** The generated client's paths already carry
 * their prefix where they should: `/api/account` has it, `/auth/login` does not
 * — because `auth` is excluded from the backend's global prefix
 * (`GLOBAL_PREFIX_EXCLUDES` in `src/configure-app.ts`: the SSO login is the
 * recovery path for a lost API key, so it cannot live behind a prefix). So
 * adding `/api` here is not "composing the URL": it is duplicating it on the
 * routes that already have it and adding it to the ones that do not.
 *
 * Valid values: the empty string (same origin, the production case) or an
 * absolute origin (`https://app.factit.cl`, to point at a remote backend in
 * development). `/api` is **not** valid.
 *
 * It exists as a module because the value was read in two places with different
 * defaults — `baseApi.ts` with `''` and `LoginPage.tsx` with `'/api'` — and that
 * broke the SSO login in production: the page built `/api/auth/login`, which is
 * no route at all. And not being one it gave no 404, it fell into the tenant
 * middleware (which runs over `{*splat}`) and answered `401 Missing API key`, an
 * error that points at the wrong side of the problem.
 */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * The SSO login's URL. It is navigated with `window.location.href` (not with
 * `fetch`) because the backend answers a 302 to the OAuth provider: the address
 * bar has to travel, not an XHR.
 *
 * It is a function and not a template at the call site so the fix above is
 * verifiable with a test: the defect lived in an assignment to
 * `window.location.href`, which no DOM test could see.
 */
export function ssoLoginUrl(base: string = API_BASE_URL): string {
  return `${base}/auth/login`;
}
